import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { DividerModule } from 'primeng/divider';
import { filter, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  clearSetPasswordFlow,
  consumeSilentB2cRecovery,
  getMalformedFirmloginRecoveryPath,
  getPostLogoutRedirectUri,
  hasPendingB2cRedirect,
  hasPendingMsalInteraction,
  isFreshLoginCallback,
  isRoleSwitchInProgress,
  msalConfig,
  relocatePendingB2cRedirect,
} from '../../auth.config';
import { FirmService } from '../../services/firmservice';
import { RoleService } from '../../services/role.service';

@Component({
  selector: 'app-firm-login',
  standalone: true,
  imports: [CommonModule, FormsModule, DividerModule],
  templateUrl: './firm-login.component.html',
  styleUrl: './firm-login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FirmLoginComponent implements OnInit {
  firmname: any = 'fispoke';
  roleType: string = '';
  firmLogoPath: string = '';
  private isAdvisorFlow = false;
  /** True when this mount followed a redirectToFirmLogin(true) after an unusable B2C callback — skip forcing a re-prompt. */
  private isSilentRecovery = false;

  circle2On: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private firmService: FirmService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private roleService: RoleService
  ) {}

  ngOnInit(): void {
    const recoveryPath = getMalformedFirmloginRecoveryPath(window.location.pathname);
    if (recoveryPath) {
      window.location.replace(recoveryPath);
      return;
    }

    if (isRoleSwitchInProgress()) {
      return;
    }

    if (hasPendingB2cRedirect()) {
      if (relocatePendingB2cRedirect(environment.mslConfig.redirectUri)) {
        return;
      }
      return;
    }

    this.isSilentRecovery = consumeSilentB2cRecovery();

    const tokenParam = (this.route.snapshot.paramMap.get('token') || '').trim();
    const roleTypeParam = (this.route.snapshot.paramMap.get('roleType') || '')
      .trim()
      .toLowerCase();

    this.firmname = tokenParam || 'fispoke';
    this.roleType = roleTypeParam;
    this.isAdvisorFlow = this.roleType === 'adv';

    localStorage.setItem('firmname', this.firmname);
    if (this.roleType) {
      localStorage.setItem('roleType', this.roleType);
    } else {
      localStorage.removeItem('roleType');
    }
    this.changeDetectorRef.markForCheck();

    setTimeout(() => {
      this.circle2On = true;
      this.changeDetectorRef.markForCheck();
    }, 1500);

    // Wait for MSAL to finish initialize + handleRedirect before touching the cache.
    // Running earlier races a login callback still being processed (accounts exist,
    // JwtToken not stored yet) and would wipe the fresh login as "stale".
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        take(1)
      )
      .subscribe(() => this.startFirmLoginFlow());
  }

  private startFirmLoginFlow(): void {
    clearSetPasswordFlow();
    if (this.hasValidSession()) {
      this.router.navigate(['/advisor-dashboard']);
      return;
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0 && isFreshLoginCallback()) {
      this.router.navigate(['/advisor-dashboard']);
      return;
    }

    if (this.hasStaleSession()) {
      this.clearStaleSession(this.firmname, this.roleType);
    }

    this.firmService
      .getFirmDetailsByName(this.firmname)
      .subscribe((data: any) => {
        let profile = data.Profile1;
        if (environment.env == 'local') {
          profile = environment.mslProfile.toLowerCase();
        }
        if (data.FirmLogo) {
          this.firmLogoPath = data.FirmLogo;
        } else {
          this.firmLogoPath =
            'https://fispokestorageaccdev.blob.core.windows.net/firmlevelrepo/FirmAzureAdTemplates/parentfirm03/images/logo.png';
        }
        localStorage.setItem('LOGO', this.firmLogoPath);

        if (profile) {
          localStorage.setItem('FIRMID', data.FirmId);
          localStorage.setItem('sso_button_name', data.sso_button_name);
          this.updateMsalAuthority(profile);
        } else {
          console.error('Invalid Authority URL:', profile);
        }
        this.changeDetectorRef.markForCheck();
      });
  }

  updateMsalAuthority(policyName: string) {
    if (!policyName) {
      console.error('Policy name is missing. Cannot update authority.');
      return;
    }

    const tenantName = environment.mslConfig.tenantName;
    const newAuthority = `https://${tenantName}.b2clogin.com/${tenantName}.onmicrosoft.com/${policyName}`;
    const sso_button_name = localStorage.getItem('sso_button_name');

    if (!this.isValidUrl(newAuthority)) {
      console.error('Invalid constructed authority URL:', newAuthority);
      return;
    }

    const startLogin = () => {
      if (hasPendingB2cRedirect()) {
        return;
      }

      msalConfig.auth.authority = newAuthority;
      msalConfig.auth.redirectUri = environment.mslConfig.redirectUri;
      msalConfig.auth.postLogoutRedirectUri = getPostLogoutRedirectUri();
      msalConfig.auth.knownAuthorities = [tenantName + '.b2clogin.com'];

      this.msalService.loginRedirect({
        authority: newAuthority,
        scopes: ['openid', 'profile', 'offline_access','https://'+environment.mslConfig.tenantName+'.onmicrosoft.com/'+environment.mslConfig.clientId+'/read'],
        extraQueryParameters: {
          // Skip forcing a fresh credential prompt when recovering from an unusable B2C
          // callback (e.g. a bookmarked authorize URL) — the B2C session cookie is still
          // warm from the credentials the user just entered, so this completes silently.
          ...(!this.isSilentRecovery && { prompt: 'login' }),
          customParam1: window.btoa(this.firmLogoPath),
          ...(this.isAdvisorFlow && { customParam2: 'adv' }),
          firmname: this.firmname,
          customParam3: sso_button_name || '',
        },
      });
    };

    if (hasPendingMsalInteraction()) {
      this.msalBroadcastService.inProgress$
        .pipe(
          filter((status) => status === InteractionStatus.None),
          take(1)
        )
        .subscribe(() => startLogin());
      return;
    }

    startLogin();
  }

  private hasValidSession(): boolean {
    return (
      !!sessionStorage.getItem('JwtToken') &&
      this.msalService.instance.getAllAccounts().length > 0
    );
  }

  private hasStaleSession(): boolean {
    const jwtToken = sessionStorage.getItem('JwtToken');
    if (jwtToken) {
      return false;
    }
    const hasAppSession =
      !!localStorage.getItem('profile') || !!localStorage.getItem('roles');
    if (hasAppSession) {
      return true;
    }
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  private clearStaleSession(firmname: string, roleType: string): void {
    sessionStorage.clear();
    this.msalService.instance.clearCache();
    this.roleService.clearAllCookies();
    localStorage.clear();
    if (firmname) {
      localStorage.setItem('firmname', firmname);
    }
    if (roleType) {
      localStorage.setItem('roleType', roleType);
    }
  }

  isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch (e) {
      console.error('Error : ' + e);
      return false;
    }
  }
}
